import { useState, useCallback } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Text,
  TextField,
  Select,
} from "@radix-ui/themes";
import { useAnonymous } from "../context/AnonymousContext";

export type ParamMode = "single" | "sweep";

export interface OperatingConditions {
  alpha_deg?: number[];
  beta_deg?: number[];
  mach?: number[];
  velocity_magnitude?: number[];
}

interface Props {
  mode: ParamMode;
  onModeChange: (m: ParamMode) => void;
  onSubmit: (params: OperatingConditions) => void;
  submitting?: boolean;
  defaultAlpha?: number;
  defaultBeta?: number;
  defaultMach?: number;
}

function parseValues(raw: string): number[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const rangeMatch = trimmed.match(/^(-?[\d.]+)\s*:\s*(-?[\d.]+)\s*:\s*(-?[\d.]+)$/);
  if (rangeMatch) {
    const [, startStr, stepStr, endStr] = rangeMatch;
    const start = Number(startStr);
    const step = Number(stepStr);
    const end = Number(endStr);
    if (step === 0 || (step > 0 && start > end) || (step < 0 && start < end)) return [start];
    const vals: number[] = [];
    for (let v = start; step > 0 ? v <= end + 1e-9 : v >= end - 1e-9; v += step) {
      vals.push(Math.round(v * 1e6) / 1e6);
      if (vals.length > 200) break;
    }
    return vals;
  }

  return trimmed.split(/[,\s]+/).map(Number).filter((n) => !isNaN(n));
}

function ValuePreview({ raw }: { raw: string }) {
  const vals = parseValues(raw);
  if (vals.length <= 1) return null;
  return (
    <Flex gap="1" wrap="wrap" mt="1">
      {vals.map((v, i) => (
        <Badge key={i} size="1" variant="soft" color="blue">
          {v}
        </Badge>
      ))}
    </Flex>
  );
}

export function SweepForm({
  mode,
  onModeChange,
  onSubmit,
  submitting = false,
  defaultAlpha,
  defaultBeta,
  defaultMach,
}: Props) {
  const [alpha, setAlpha] = useState(defaultAlpha?.toString() ?? "0");
  const [beta, setBeta] = useState(defaultBeta?.toString() ?? "0");
  const [mach, setMach] = useState(defaultMach?.toString() ?? "");
  const [velocity, setVelocity] = useState("");
  const { isAnonymous } = useAnonymous();
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const handleSubmit = useCallback(() => {
    if (isAnonymous) {
      setDemoModalOpen(true);
      return;
    }
    const params: OperatingConditions = {};
    const alphaVals = parseValues(alpha);
    const betaVals = parseValues(beta);
    const machVals = parseValues(mach);
    const velVals = parseValues(velocity);

    if (alphaVals.length) params.alpha_deg = alphaVals;
    if (betaVals.length) params.beta_deg = betaVals;
    if (machVals.length) params.mach = machVals;
    if (velVals.length) params.velocity_magnitude = velVals;

    onSubmit(params);
  }, [alpha, beta, mach, velocity, onSubmit]);

  const isSweep = mode === "sweep";
  const alphaCount = parseValues(alpha).length;
  const betaCount = parseValues(beta).length;
  const machCount = parseValues(mach).length;
  const totalCombos =
    (alphaCount || 1) * (betaCount || 1) * (machCount || 1);

  return (
    <Box>
      <Flex align="center" gap="2" mb="3">
        <Text size="2" weight="medium" style={{ minWidth: 50 }}>
          Mode
        </Text>
        <Select.Root
          value={mode}
          onValueChange={(v) => onModeChange(v as ParamMode)}
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="single">Single Fork</Select.Item>
            <Select.Item value="sweep">Parameter Sweep</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>

      {isSweep && (
        <Card size="1" mb="3" style={{ background: "var(--accent-a2)" }}>
          <Text size="1" color="blue">
            Enter comma-separated values (e.g. <strong>-2, 0, 2, 4, 6, 8</strong>)
            or a range as <strong>start:step:end</strong> (e.g. <strong>-2:2:12</strong>).
            All combinations will be submitted.
          </Text>
        </Card>
      )}

      <Flex direction="column" gap="2" mb="3">
        <Box>
          <Flex align="center" gap="2">
            <Text size="2" style={{ minWidth: 120 }}>
              Alpha (deg)
            </Text>
            <TextField.Root
              value={alpha}
              onChange={(e) => setAlpha(e.target.value)}
              placeholder={isSweep ? "-2, 0, 2, 4, 6, 8, 10, 12" : "4"}
              style={{ flex: 1 }}
            />
            {isSweep && alphaCount > 0 && (
              <Badge size="1" variant="surface">
                {alphaCount}
              </Badge>
            )}
          </Flex>
          {isSweep && <ValuePreview raw={alpha} />}
        </Box>

        <Box>
          <Flex align="center" gap="2">
            <Text size="2" style={{ minWidth: 120 }}>
              Beta (deg)
            </Text>
            <TextField.Root
              value={beta}
              onChange={(e) => setBeta(e.target.value)}
              placeholder={isSweep ? "0" : "0"}
              style={{ flex: 1 }}
            />
            {isSweep && betaCount > 0 && (
              <Badge size="1" variant="surface">
                {betaCount}
              </Badge>
            )}
          </Flex>
          {isSweep && <ValuePreview raw={beta} />}
        </Box>

        <Box>
          <Flex align="center" gap="2">
            <Text size="2" style={{ minWidth: 120 }}>
              Mach
            </Text>
            <TextField.Root
              value={mach}
              onChange={(e) => setMach(e.target.value)}
              placeholder={isSweep ? "0.2, 0.3, 0.4" : "0.3"}
              style={{ flex: 1 }}
            />
            {isSweep && machCount > 0 && (
              <Badge size="1" variant="surface">
                {machCount}
              </Badge>
            )}
          </Flex>
          {isSweep && <ValuePreview raw={mach} />}
        </Box>

        <Box>
          <Flex align="center" gap="2">
            <Text size="2" style={{ minWidth: 120 }}>
              Velocity (m/s)
            </Text>
            <TextField.Root
              value={velocity}
              onChange={(e) => setVelocity(e.target.value)}
              placeholder="(optional, overrides Mach)"
              style={{ flex: 1 }}
            />
          </Flex>
        </Box>
      </Flex>

      {isSweep && totalCombos > 1 && (
        <Card size="1" mb="2">
          <Flex justify="between" align="center">
            <Text size="2">Total cases</Text>
            <Text size="3" weight="bold" color="blue">
              {totalCombos}
            </Text>
          </Flex>
          {alphaCount > 1 && betaCount > 1 && (
            <Text size="1" color="gray">
              {alphaCount} alpha x {betaCount} beta
              {machCount > 1 ? ` x ${machCount} Mach` : ""}
            </Text>
          )}
        </Card>
      )}

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        size="3"
        style={{ width: "100%" }}
      >
        {submitting
          ? "Submitting..."
          : isSweep
            ? `Submit Sweep (${totalCombos} case${totalCombos !== 1 ? "s" : ""})`
            : "Fork Case"}
      </Button>

      <Dialog.Root open={demoModalOpen} onOpenChange={setDemoModalOpen}>
        <Dialog.Content maxWidth="480px">
          <Dialog.Title>This is a demo</Dialog.Title>
          <Text as="p" size="2" color="gray" style={{ lineHeight: 1.6 }}>
            In production, Thread submits your sweep directly to the Flow360
            solver — one of many ways to interact with your CFD toolchain
            from inside the provenance graph. New results flow back into the
            DAG automatically.
          </Text>
          <Text as="p" size="2" color="gray" mt="2" style={{ lineHeight: 1.6 }}>
            Run submission is disabled in the public demo.
          </Text>
          <Flex justify="end" mt="4">
            <Button variant="soft" onClick={() => setDemoModalOpen(false)}>
              Got it
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}
